import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faArrowDown,
  faCalendar,
  faCheck,
  faChevronDown,
} from '@fortawesome/pro-solid-svg-icons'
import {
  faArrowUpRight,
  faCircleCheck as faCircleCheckRegular,
  faCircleQuestion as faCircleQuestionRegular,
  faClock as faClockRegular,
  faPen,
  faRepeat,
  faShieldCheck as faShieldCheckRegular,
  faTag,
  faTriangleExclamation as faTriangleExclamationRegular,
} from '@fortawesome/pro-regular-svg-icons'

import { Accordion, Badge, Button, Icon, Link, Pill, Text } from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'
import { logHeardError } from '@/utils/errorHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS_LUXON, formatISOFromUTC } from '@/utils/dateHelpers'

import { ANALYTICS_SURFACES } from '../analytics/compliance-analytics.constants'
import { useComplianceAnalytics } from '../analytics/use-compliance-analytics'
import { useComplianceVerdict } from '../analytics/use-compliance-verdict'
import { selectLicenses } from '../compliance.selectors'
import { setSectionIndex } from '../compliance.slice'
import { KeepInTouchCard } from '@/features/user/signup/KeepInTouchCard'

import { ReportEndCard } from './ReportEndCard'
import { ReportStickyCta } from './ReportStickyCta'
import { REPORT_END_CARD_ID } from './useReachedEndCard'
import { useReportEndCardSlot } from './useReportEndCardSlot'
import type {
  ComplianceReport,
  DisplayTier,
  FactConsequence,
  ReportArea,
  ReportFact,
  ReportObligation,
} from '../lib/types'
import {
  COMPLIANCE_PATHS,
  FUNNEL_PAGE_CLS,
  licensedStateCodes,
  stateName,
} from '../lib/constants'
import { UrgencyBadge } from './status'
import {
  ReportDisclaimerFooter,
  ReportDisclaimerLead,
} from './ReportDisclaimer'
import { emphasizeDates } from '../lib/emphasizeDates'
import { useExpandCollapse } from './useExpandCollapse'

import './report-print.css'

const COUNT_TILE_META = {
  orange: {
    icon: faTriangleExclamationRegular,
    color: 'text-orange-200',
    backgroundColor: 'priority2' as const,
    backgroundClassName: undefined as string | undefined,
  },
  blue: {
    icon: faCircleQuestionRegular,
    color: 'text-blue-200',
    backgroundColor: 'transparent' as const,
    backgroundClassName: 'bg-blue-25',
  },
  green: {
    icon: faCircleCheckRegular,
    color: 'text-green-100',
    backgroundColor: 'transparent' as const,
    backgroundClassName: 'bg-green-25',
  },
  neutral: {
    icon: faClockRegular,
    color: 'text-neutral-500',
    backgroundColor: 'level2' as const,
    backgroundClassName: undefined as string | undefined,
  },
}

const CountTile: React.FC<{
  count: number
  label: string
  accent: 'orange' | 'blue' | 'green' | 'neutral'
  onClick: () => void
}> = ({ count, label, accent, onClick }) => {
  const meta = COUNT_TILE_META[accent]
  return (
    <button
      type="button"
      onClick={onClick}
      data-report-print-static
      className="flex flex-1 flex-col gap-3 rounded-xl border border-neutral-100 bg-neutral-0 p-4 text-left transition hover:border-neutral-300 hover:shadow-sm"
    >
      <div className="flex items-center gap-3">
        <Icon
          icon={meta.icon}
          size="sm"
          color={meta.color}
          backgroundColor={meta.backgroundColor}
          backgroundClassName={meta.backgroundClassName}
          backgroundStyle="rounded"
        />
        <span className="font-primary text-3xl text-neutral-900">{count}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
          {label}
        </Text>
        <span data-report-print-affordance>
          <Icon
            icon={faArrowDown}
            size="2xs"
            color="text-neutral-400"
            backgroundColor="level0"
            backgroundStyle="circle"
          />
        </span>
      </div>
    </button>
  )
}

/** `1 is`, `3 are`. These buckets hit 1 routinely. */
const agree = (count: number, singular: string, plural: string): string =>
  `${count} ${count === 1 ? singular : plural}`

const ReportHero: React.FC<{
  report: ComplianceReport
  onJump: (tier: GroupTier) => void
}> = ({ report, onJump }) => {
  const { context, counts } = report
  // Every tier is counted now that `good_to_know` sits in Worth watching and is
  // counted by its tile: the total has to match the sum of the tiles beside it.
  // `good_to_know` counts toward Worth watching, so it counts toward the total
  // too. The five tiers partition every obligation in the report.
  const worthWatching = counts.notRelevant + counts.goodToKnow
  const totalObligations =
    counts.attention + counts.needsInfo + counts.covered + worthWatching
  // Itemises every bucket the total counts, so the numbers in the sentence always
  // sum to it. `needsInfo` is conditional on the same `> 0` test as its own tile
  // below: naming a bucket the tile row omits would have the two disagree in
  // exactly the cases where one of them is empty.
  const rollupParts = [
    `${agree(counts.attention, 'needs', 'need')} a look`,
    ...(counts.needsInfo > 0
      ? [`${agree(counts.needsInfo, 'needs', 'need')} you to tell us more`]
      : []),
    `${agree(counts.covered, 'is', 'are')} already covered`,
    `${agree(worthWatching, 'is', 'are')} worth keeping an eye on`,
  ]
  return (
    <header
      className="flex flex-col gap-5 rounded-2xl bg-gradient-to-b from-yellow-25/90 to-moss-25/0 p-8"
      data-report-print-card
      data-report-print-hero
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
          {`Your practice readiness · ${formatISOFromUTC(
            report.generatedAt,
            DATE_FORMATS_LUXON.DISPLAY_LONG_SHORT_DAY
          )}`}
        </Text>
        <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
          {[
            context.jurisdictions.filter((j) => j !== 'federal').join(', '),
            context.profession,
            context.entityType,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Text>
      </div>
      {/* Consecutive paragraphs, not three separated statements. */}
      <div className="flex flex-col gap-2">
        <Text variant={TypographyStyles.DISPLAY_MD} color={TextColors.HEADER}>
          {report.headline}
        </Text>
        <Text variant={TypographyStyles.BODY_MD}>{report.summary}</Text>
        <Text variant={TypographyStyles.BODY_MD} className="text-neutral-600">
          {`We reviewed ${totalObligations} ${
            totalObligations === 1 ? 'obligation' : 'obligations'
          } across your practice. ${rollupParts.join(', ')}.`}
        </Text>
      </div>
      {/* Outside the prose group so the header's `gap-5` separates them. */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <CountTile
          count={counts.attention}
          label="need a look"
          accent="orange"
          onClick={() => onJump('attention')}
        />
        {counts.needsInfo > 0 && (
          <CountTile
            count={counts.needsInfo}
            label="tell us more"
            accent="blue"
            onClick={() => onJump('needs_info')}
          />
        )}
        <CountTile
          count={counts.covered}
          label="covered"
          accent="green"
          onClick={() => onJump('covered')}
        />
        <CountTile
          count={worthWatching}
          label="worth watching"
          accent="neutral"
          onClick={() => onJump('not_relevant')}
        />
      </div>
      {/* Inside the hero rather than down in `ReportDisclaimerFooter`, so it
          still sits with the claims it qualifies. */}
      <ReportDisclaimerLead />
    </header>
  )
}

const CADENCE_LABELS: Record<string, string> = {
  annual: 'Every year',
  biennial: 'Every 2 years',
  every_2_years: 'Every 2 years',
  triennial: 'Every 3 years',
  every_5_years: 'Every 5 years',
  every_120_days: 'About every 120 days',
  quarterly: 'Quarterly',
  one_time: 'One time',
  annual_formation_month: 'Every year (formation month)',
}
const cadenceLabel = (cadence: string): string =>
  CADENCE_LABELS[cadence] ?? cadence.replace(/_/g, ' ')

const DetailField: React.FC<{
  label: string
  value: string
  icon: IconDefinition
}> = ({ label, value, icon }) => (
  <div className="flex items-center gap-2">
    <Icon icon={icon} color="text-neutral-400" />
    <Text variant={TypographyStyles.LABEL_XS} color={TextColors.MUTED}>
      {label}
    </Text>
    <Text variant={TypographyStyles.BODY_XS} className="capitalize">
      {value}
    </Text>
  </div>
)

/**
 * What each consequence actually costs you, phrased as the risk of letting the
 * obligation slip. Kept qualitative on purpose: `consequence` is a category,
 * and the facts carry no penalty amount to put a figure to (`cost` is the
 * price of complying — a renewal fee — not the price of missing it, and
 * `stakeCents` is still a TODO on the composer). Inventing a dollar amount for
 * a licensing or tax penalty is the one thing this surface must not do, since
 * a reader would act on it.
 */
const CONSEQUENCE_RISK: Record<FactConsequence, string | null> = {
  practice_barred:
    'Letting this lapse can bar you from practicing until it’s resolved.',
  renewal_blocked: 'Letting this lapse can block your license renewal.',
  income_scaled_penalty:
    'The penalty is based on the amount of tax underpaid or deposited late, so a larger shortfall can cost more.',
  penalty: 'Not doing this can carry a financial penalty.',
  deactivation: 'Letting this lapse can deactivate your enrollment.',
  personal_liability:
    'Without this, you’re personally liable rather than your practice.',
  audit_risk: 'Gaps here are what raise your audit exposure.',
  none: null,
}

/** Pins whichever card the rail carries, but only on a viewport tall enough to
 *  seat it — see the note at the call site. Sticky fails silently when the element
 *  outgrows the scrollport, so the floor has to clear the tallest thing the rail
 *  can hold.
 *
 *  Measured at the rail's 300px, against the budget the floor leaves (viewport
 *  less `top-6` and the same again as bottom slack):
 *
 *    at 700px  budget 652 — next steps 458, keep in touch 372
 *    at 900px  budget 852 — next steps 654, keep in touch 372
 *
 *  Both clear it at both ends. The next-steps card is the tall one, not the
 *  keep-in-touch card: at the floor its illustration is hidden by its own
 *  `min-height:760px` gate, and keep-in-touch simply carries less. Keep-in-touch
 *  does not vary with viewport height at all. The `max-h` below still backstops
 *  a copy change that outgrows this. */
/** The report's two-column geometry. One grid, three cells: the heading row and the
 *  obligations stack down the first column, and the rail spans both rows of the second.
 *  Spanning is what lets the rail start level with the heading while still having a
 *  containing block tall enough for its sticky card to travel the whole report. */
const REPORT_GRID_CLS = 'grid grid-cols-1 gap-8 lg:grid-cols-[1fr_300px]'

const STICKY_CTA_CLS = [
  'lg:[@media(min-height:700px)]:sticky',
  'lg:[@media(min-height:700px)]:top-6',
  'lg:[@media(min-height:700px)]:max-h-[calc(100vh-3rem)]',
  'lg:[@media(min-height:700px)]:overflow-y-auto',
].join(' ')

/** `CONSEQUENCE_RISK` is keyed by a union that mirrors the API's enum by hand,
 *  so a value the server gained isn't a type error — it's `undefined` here. */
const consequenceRisk = (consequence: FactConsequence | null): string | null =>
  (consequence && CONSEQUENCE_RISK[consequence]) || null

/**
 * Reports consequence values the copy table doesn't know, once per report.
 *
 * The fact worth knowing is "the server has a value we don't", a property of
 * the payload rather than of a row. Checking during render logged it once per
 * matching obligation per accordion toggle instead — noise proportional to how
 * much the reader clicked, for a signal that should arrive once.
 */
const useUnmappedConsequenceReport = (areas: ReportArea[]): void => {
  useEffect(() => {
    const unmapped = [
      ...new Set(
        areas
          .flatMap((a) => a.obligations)
          .map((o) => o.consequence)
          .filter(
            (c): c is FactConsequence =>
              Boolean(c) && CONSEQUENCE_RISK[c as FactConsequence] === undefined
          )
      ),
    ]
    if (unmapped.length === 0) return
    logHeardError(
      new Error(`unmapped obligation consequences: ${unmapped.join(', ')}`),
      { context: 'compliance: report obligation risk' }
    )
  }, [areas])
}

/**
 * Picks which risk line a multi-state card leads with. Mirrors the composer's
 * `CONSEQUENCE_SEVERITY`: the payload carries the category, not its rank.
 */
const CONSEQUENCE_RANK: Record<FactConsequence, number> = {
  practice_barred: 6,
  deactivation: 5,
  personal_liability: 4,
  renewal_blocked: 3,
  income_scaled_penalty: 3,
  penalty: 2,
  audit_risk: 1,
  none: 0,
}
const consequenceRank = (c: FactConsequence | null): number =>
  (c && CONSEQUENCE_RANK[c]) || 0

/**
 * One obligation across every jurisdiction it applies in. The composer fans a
 * `per_licensed_state` obligation out per state; the reader has one thing to do.
 */
interface ObligationCard {
  templateKey: string
  title: string
  /** Every instance, ordered by jurisdiction. Never empty. */
  instances: ReportObligation[]
}

const stateLabel = (code: string): string => stateName(code) || code

/**
 * One card per `templateKey`, keeping each per-state instance for the detail.
 * Insertion order is preserved so the composer's ordering drives the stack.
 */
const collapseByTemplate = (
  obligations: ReportObligation[]
): ObligationCard[] => {
  const byKey = new Map<string, ReportObligation[]>()
  for (const o of obligations) {
    const list = byKey.get(o.templateKey)
    if (list) list.push(o)
    else byKey.set(o.templateKey, [o])
  }
  return [...byKey.values()].map((group) => {
    const instances = [...group].sort((a, b) =>
      (a.contextKey ?? '').localeCompare(b.contextKey ?? '')
    )
    return {
      templateKey: instances[0].templateKey,
      title: instances[0].title,
      instances,
    }
  })
}

/** The instance whose due date lands first — what the collapsed card's badge
 *  should show, since that's the one the reader has to act on soonest. */
const soonestDue = (instances: ReportObligation[]): ReportObligation =>
  instances.reduce((soonest, o) => {
    if (!o.dueAt) return soonest
    if (!soonest.dueAt) return o
    return o.dueAt < soonest.dueAt ? o : soonest
  })

/** The worst consequence across a card's jurisdictions — one state's harsher
 *  statute shouldn't be hidden because another state's is milder. */
const leadRisk = (instances: ReportObligation[]): string | null =>
  consequenceRisk(
    instances.reduce((worst, o) =>
      consequenceRank(o.consequence) > consequenceRank(worst.consequence)
        ? o
        : worst
    ).consequence
  )

const printableSource = (href: string): string => {
  try {
    const url = new URL(href)
    return `${url.hostname}${url.pathname}${url.search}${url.hash}`
  } catch {
    return href
  }
}

/** A fact's source, linked out. Shared, so the lead rule's link and the ones
 *  under it cannot drift apart. */
const SourceLink: React.FC<{ href: string }> = ({ href }) => (
  <Link
    href={href}
    newPage
    onClick={(e) => e.stopPropagation()}
    className="flex w-fit items-center gap-1 text-xs text-neutral-600"
  >
    <span className="flex items-center gap-1" data-print-exclude>
      View Source
      <Icon
        icon={faArrowUpRight}
        size="2xs"
        color="text-neutral-600"
        backgroundClassName="h-auto w-auto"
      />
    </span>
    <span data-report-print-only data-report-print-source>
      {`View source: ${printableSource(href)}`}
    </span>
  </Link>
)

/**
 * The other verified rules under the same obligation, which the composer carries
 * in `supportingFacts` and nothing rendered. Each keeps its own description and
 * source: they are separate duties under separate citations.
 *
 * Closed by default, and unstyled beyond the disclosure. The card's own detail
 * is already open here, and the worst card carries seven of these.
 */
const SupportingRulesList: React.FC<{ facts: ReportFact[] }> = ({ facts }) => (
  <div className="flex flex-col gap-3">
    {facts.map((fact) => (
      <div key={fact.id} className="flex flex-col gap-1">
        {fact.description && (
          <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
            {fact.description}
          </Text>
        )}
        {fact.sourceUrl && <SourceLink href={fact.sourceUrl} />}
      </div>
    ))}
  </div>
)

const SupportingRules: React.FC<{ facts: ReportFact[] }> = ({ facts }) =>
  facts.length > 0 ? (
    <>
      <div
        className="mt-2"
        data-print-exclude
        // The card is itself a button that toggles this detail shut, so without
        // this the opening click closes everything.
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="presentation"
      >
        <Accordion variant="text" type="single" collapsible>
          <Accordion.Item value="supporting-rules">
            {/* Overrides the variant's `w-full justify-between py-2 gap-x-2`,
                which strands the chevron at the far edge of the card. */}
            <Accordion.Trigger className="w-fit justify-start gap-x-0 py-0">
              <Text
                variant={TypographyStyles.LABEL_XS}
                color={TextColors.MUTED}
              >
                {facts.length === 1
                  ? 'Also required under this obligation'
                  : `Also required under this obligation (${facts.length})`}
              </Text>
            </Accordion.Trigger>
            <Accordion.Content>
              <SupportingRulesList facts={facts} />
            </Accordion.Content>
          </Accordion.Item>
        </Accordion>
      </div>
      <div
        className="mt-2"
        data-report-print-only
        data-report-print-supporting-rules
      >
        <SupportingRulesList facts={facts} />
      </div>
    </>
  ) : null

/** One jurisdiction's detail. Description, source and cadence all vary by state,
 *  so each gets its own block rather than one merged blob. */
const InstanceDetail: React.FC<{
  obligation: ReportObligation
  /** Only when the collapsed row cannot show one rule for every state. */
  showGuidance: boolean
  /** Same reasoning as `showGuidance`: with a single instance the header badge
   *  already carries this obligation's date, so repeating it here says nothing. */
  showUrgency: boolean
}> = ({ obligation, showGuidance, showUrgency }) => {
  const fact = obligation.fact
  // The obligation's own description is the preferred source: the API resolves it
  // as `fact.description ?? CMS copy`, so it is present even with no fact. The
  // fact fallback covers an API that does not yet send the field.
  const description = obligation.description ?? fact?.description ?? null
  const hasCost = Boolean(obligation.costLabel && obligation.costLabel !== '—')
  const state = obligation.contextKey ? stateLabel(obligation.contextKey) : null
  return (
    <div className="flex flex-col gap-2">
      {state && (
        <div className="flex items-center justify-between gap-3">
          <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
            {state}
          </Text>
          {/* The collapsed row shows one badge for the whole group, from the
              soonest instance, so a state that is overdue while another is not
              had no way to say so. */}
          {showUrgency && (
            <UrgencyBadge
              status={obligation.status}
              urgency={obligation.urgency}
              dueAt={obligation.dueAt}
              highStakes={false}
            />
          )}
        </div>
      )}
      {obligation.schedule.length > 0 && (
        <div className="flex flex-col gap-2">
          {obligation.schedule.map((installment) => (
            <div
              key={installment.dueAt}
              className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2"
            >
              <Text variant={TypographyStyles.LABEL_SM}>
                {installment.label}
              </Text>
              <Badge
                variant="secondary"
                color="white"
                size="sm"
                icon={faCalendar}
              >
                {installment.displayDueAt}
              </Badge>
            </div>
          ))}
        </div>
      )}
      {description && (
        <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
          {emphasizeDates(description)}
        </Text>
      )}
      {showGuidance && obligation.guidance && (
        <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
          {obligation.guidance}
        </Text>
      )}
      {fact?.sourceUrl && <SourceLink href={fact.sourceUrl} />}
      {obligation.sourcePending && (
        <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
          We haven’t confirmed the rules for your state yet. Your state’s
          business registry or licensing board can give you the exact deadline
          and fee.
        </Text>
      )}
      {(fact?.cadence || hasCost) && (
        // Adds to the container's `gap-2` for 24px of clearance from the
        // source link, while description-to-link stays at 8.
        <div className="mt-4 flex flex-wrap gap-6">
          {fact?.cadence && (
            <DetailField
              label="Frequency"
              value={cadenceLabel(fact.cadence)}
              icon={faRepeat}
            />
          )}
          {hasCost && (
            <DetailField
              label="Cost"
              value={obligation.costLabel}
              icon={faTag}
            />
          )}
        </div>
      )}
      {/* Last in the block, below the lead rule's own frequency and cost. */}
      <SupportingRules facts={obligation.supportingFacts} />
    </div>
  )
}

/** Each jurisdiction's block, ruled off from the next. */
const InstanceList: React.FC<{ instances: ReportObligation[] }> = ({
  instances,
}) => (
  <div className="flex flex-col">
    {instances.map((o, i) => (
      <div
        key={o.contextKey ?? o.templateKey}
        className={mtc(i > 0 && 'mt-4 border-t border-neutral-100 pt-4')}
      >
        <InstanceDetail
          obligation={o}
          showGuidance={instances.length > 1}
          showUrgency={instances.length > 1}
        />
      </div>
    ))}
  </div>
)

/** How many jurisdictions a collapsed row covers. The names themselves head
 *  each block inside the detail; collapsed, the reader only needs to know there
 *  is more than one. */
const StateCount: React.FC<{ instances: ReportObligation[] }> = ({
  instances,
}) =>
  instances.length > 1 ? (
    <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
      {`${instances.length} states`}
    </Text>
  ) : null

/** Kept in step with `QUARTERLY_TAXES_KEY` in the API's `report_composer.ts`,
 *  which sorts this obligation to the head of its area. */
const QUARTERLY_TAXES_KEY = 'pay_estimated_taxes'

export const hasDetailFor = (o: ReportObligation): boolean =>
  Boolean(
    o.description ||
    o.fact?.description ||
    o.fact?.cadence ||
    o.fact?.sourceUrl ||
    (o.costLabel && o.costLabel !== '\u2014') ||
    o.schedule.length > 0 ||
    o.guidance ||
    o.sourcePending ||
    o.supportingFacts.length > 0
  )

const ObligationStep: React.FC<{
  card: ObligationCard
  tier: DisplayTier
  highStakes: boolean
  area: string
  /** 1-based rank of the card inside the group it renders in. */
  position: number
}> = ({ card, tier, highStakes, area, position }) => {
  const [expanded, setExpanded] = useState(false)
  const { trackExpandedReportObligation } = useComplianceAnalytics(
    ANALYTICS_SURFACES.SignupFunnel
  )
  const expandRef = useExpandCollapse(expanded)
  const lead = card.instances[0]
  const multiState = card.instances.length > 1
  const isCovered = tier === 'covered'
  const hasDetail = multiState || card.instances.some(hasDetailFor)
  // Clipped while closed, replaced by the full text on open. From the lead
  // instance: one row cannot show every state's wording.
  const preview = lead.description ?? lead.fact?.description ?? null
  const toggle = (): void => {
    const next = !expanded
    setExpanded(next)
    // Only the opening half: the event is named for expansion, and firing on
    // the close too would double every count.
    if (next)
      trackExpandedReportObligation({
        obligation_key: card.templateKey,
        status: tier,
        area,
        position,
      })
  }
  // Only rows that need doing. Elsewhere there is no live risk to lead with.
  const riskCopy = tier === 'attention' ? leadRisk(card.instances) : null
  const leads = tier === 'attention' && card.templateKey === QUARTERLY_TAXES_KEY
  const due = soonestDue(card.instances)

  return (
    <div
      data-report-print-card
      data-report-print-obligation
      data-report-print-static={hasDetail ? true : undefined}
      role={hasDetail ? 'button' : undefined}
      tabIndex={hasDetail ? 0 : undefined}
      aria-expanded={hasDetail ? expanded : undefined}
      onClick={hasDetail ? toggle : undefined}
      onKeyDown={
        hasDetail
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                toggle()
              }
            }
          : undefined
      }
      className={mtc(
        'relative flex flex-col rounded-xl border border-neutral-200 p-4',
        tier === 'attention' ? 'bg-orange-0' : 'bg-neutral-0',
        hasDetail && 'cursor-pointer transition hover:border-neutral-300',
        leads && 'border-orange-100'
      )}
    >
      {/* Above the title, and visible while collapsed: what's at stake is
          what makes someone open the row, so it has to be readable before
          they do. `Icon` sizes its wrapper span off `size` — a box far larger
          than the glyph — so that box is collapsed to the glyph, or it reads
          as padding either side. */}
      {riskCopy && (
        <div className={mtc('pb-1', hasDetail && 'pr-6')}>
          {/* `ORANGE`, not `PRIORITY`: the priority token resolves to
              orange-100, a step lighter than this needs to be. */}
          <Text variant={TypographyStyles.LABEL_XS} color={TextColors.ORANGE}>
            {riskCopy}
          </Text>
        </div>
      )}
      {/* Stacked below `sm`: the title and its urgency badge together need more
          than a phone's width, and squeezing them onto one row wraps the title
          into a narrow ragged column beside the badge. The badge drops to its
          own line, left-aligned under the title it belongs to. */}
      <div
        className={mtc(
          'flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between',
          hasDetail && 'pr-6'
        )}
      >
        <div className="flex w-full min-w-0 flex-1 items-center gap-3 sm:w-auto">
          {/* Only covered rows carry an icon. Inside a Covered group the
              status was otherwise held by the group heading alone, leaving
              these indistinguishable from `needs_info` rows. */}
          {isCovered && (
            <Icon
              icon={faCheck}
              size="sm"
              color="text-green-100"
              backgroundClassName="h-auto w-auto shrink-0"
            />
          )}
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Text variant={TypographyStyles.LABEL_SM}>{card.title}</Text>
            <StateCount instances={card.instances} />
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <UrgencyBadge
            status={lead.status}
            urgency={due.urgency}
            dueAt={due.dueAt}
            highStakes={highStakes}
            overdueCount={
              card.instances.filter((o) => o.urgency === 'expired').length
            }
            periodLabel={due.schedule[0]?.period ?? null}
          />
        </div>
        {/* Pinned to the card rather than carried by the title row: the row
            sits lower when a consequence is present, which moved the chevron
            with it. `top-4`/`right-4` match the card's own padding, so it
            lands on the content's top-right corner either way. */}
        {hasDetail && (
          <span data-report-print-affordance>
            <Icon
              icon={faChevronDown}
              size="2xs"
              color="text-neutral-400"
              backgroundClassName="absolute right-4 top-4 h-auto w-auto"
              className={mtc('transition-transform', expanded && 'rotate-180')}
            />
          </span>
        )}
      </div>
      {/* `line-clamp-1`, never `truncate`: nowrap in the `1fr` track widens the
          whole report to the longest description. */}
      {!expanded && preview && (
        <div className="pt-2" data-report-print-preview>
          <Text
            variant={TypographyStyles.BODY_SM}
            color={TextColors.MUTED}
            className="line-clamp-1"
          >
            {preview}
          </Text>
        </div>
      )}
      {/* The rule differs per state, so one state's text cannot stand for all
          of them. Multi-state cards carry it per block inside the detail. */}
      {!multiState && lead.guidance && (
        <div className="pt-2">
          <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
            {lead.guidance}
          </Text>
        </div>
      )}
      {/* Outside the block above, and carrying its own top spacing inside the
          collapsing element rather than as a gap outside it. A gap is applied
          between flex children whatever their size, so a collapsed region —
          zero height, but still a child — was still reserving space under the
          row. The padding here is clipped along with everything else while
          the height is zero. */}
      {hasDetail && (
        <div ref={expandRef} data-report-print-expand>
          {/* Rule and spacing both sit inside the collapsing element, so a
              closed row still contributes nothing — see the note above. */}
          <div className="mt-3 border-t border-neutral-100 pt-3">
            <InstanceList instances={card.instances} />
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * A `good_to_know` obligation as a tickable line. No answer of ours resolves
 * these, so the reader is the only one who can say whether one is handled.
 */
/** The tiers that get a top-level section. `good_to_know` is deliberately absent:
 *  it rides inside the areas under Worth watching rather than standing alone.
 *  It sat under Needs a look before, where a heading meaning "work owed"
 *  mislabelled rules that no answer of ours resolves. */
type GroupTier = Exclude<DisplayTier, 'good_to_know'>

const GROUP_TIERS: GroupTier[] = [
  'attention',
  'needs_info',
  'covered',
  'not_relevant',
]

const GROUP_META: Record<
  GroupTier,
  { title: string; icon: IconDefinition; color: string; defaultOpen: boolean }
> = {
  attention: {
    title: 'Needs a look',
    icon: faTriangleExclamationRegular,
    color: 'text-orange-200',
    defaultOpen: true,
  },
  needs_info: {
    title: 'Tell us more',
    icon: faCircleQuestionRegular,
    color: 'text-blue-200',
    defaultOpen: true,
  },
  covered: {
    title: 'Covered',
    icon: faCircleCheckRegular,
    color: 'text-green-100',
    defaultOpen: true,
  },
  not_relevant: {
    title: 'Worth watching',
    icon: faClockRegular,
    color: 'text-neutral-500',
    defaultOpen: false,
  },
}

interface AreaBucket {
  key: string
  label: string
  stakeLabel: string
  narrative: string
  benefit: string | null
  /** True in exactly one section per area, so the narrative, benefit and stake
   *  render once. `needs_info` is not a `ReportStatus`, so an area whose only
   *  obligations are `needs_info` claims it there or nowhere. */
  isPrimary: boolean
  cards: ObligationCard[]
}

const AREA_CARD_ACCENT: Record<
  GroupTier,
  { circleBg: string; circleText: string }
> = {
  attention: { circleBg: 'bg-orange-25', circleText: 'text-orange-200' },
  needs_info: { circleBg: 'bg-blue-25', circleText: 'text-blue-200' },
  covered: { circleBg: 'bg-green-25', circleText: 'text-green-100' },
  not_relevant: { circleBg: 'bg-neutral-50', circleText: 'text-neutral-700' },
}

const STAKE_LABEL_STYLE: Record<
  string,
  { variant: 'default' | 'secondary'; color: 'orange' | 'yellow' | 'gray' }
> = {
  'High stakes if it slips': { variant: 'default', color: 'orange' },
  'Worth staying ahead of': { variant: 'secondary', color: 'yellow' },
  'Minor if it slips': { variant: 'secondary', color: 'yellow' },
  'Low stakes': { variant: 'secondary', color: 'gray' },
}

/** `good_to_know` has no section of its own: a standing rule no answer of ours
 *  resolves belongs with the rest of Worth watching, and renders there as an
 *  ordinary obligation. */
const rendersIn = (displayTier: DisplayTier, tier: GroupTier): boolean =>
  displayTier === tier ||
  (tier === 'not_relevant' && displayTier === 'good_to_know')

const tierHoldsContent = (area: ReportArea, tier: GroupTier): boolean =>
  area.obligations.some((o) => rendersIn(o.displayTier, tier))

/**
 * The one section an area shows its narrative, stake and benefit in: the first
 * it appears in, in reading order.
 *
 * Derived from the sections the area actually reaches rather than from
 * `a.status`. `needs_info` is not a `ReportStatus` so it never matched, and an
 * area can carry an `attention` status while every one of its obligations lands
 * in some other tier, which left the narrative with nowhere to render.
 */
const primaryTierFor = (area: ReportArea): GroupTier | null =>
  GROUP_TIERS.find((tier) => tierHoldsContent(area, tier)) ?? null

/**
 * One tier's obligations, grouped by area and collapsed by template. Worth
 * watching also takes each area's `good_to_know` items, so an area holding
 * only those still appears.
 */
const bucketAreasByTier = (
  areas: ReportArea[],
  tier: GroupTier
): AreaBucket[] =>
  areas
    .map((a) => ({
      key: a.key,
      label: a.label,
      stakeLabel: a.stakeLabel,
      narrative: a.narrative,
      benefit: a.benefit,
      isPrimary: primaryTierFor(a) === tier,
      cards: collapseByTemplate(
        a.obligations.filter((o) => rendersIn(o.displayTier, tier))
      ),
    }))
    .filter((b) => b.cards.length > 0)

const AreaObligationGroup: React.FC<{
  bucket: AreaBucket
  tier: GroupTier
  rank: number
}> = ({ bucket, tier, rank }) => {
  const accent = AREA_CARD_ACCENT[tier]
  const highStakes = bucket.stakeLabel === 'High stakes if it slips'
  const stakeStyle = bucket.stakeLabel
    ? (STAKE_LABEL_STYLE[bucket.stakeLabel] ?? {
        variant: 'secondary' as const,
        color: 'gray' as const,
      })
    : null
  return (
    <div
      className="flex flex-col gap-6 rounded-2xl border border-neutral-100 bg-neutral-0 p-5"
      data-report-print-area
      data-report-print-card
    >
      <div className="flex flex-col gap-2" data-report-print-area-heading>
        {/* `flex-col-reverse` below `sm`, so the stake pill, the later sibling,
            reads above the rank and area name rather than squeezed beside them:
            at phone width a pill like "High stakes if it slips" takes most of
            the row and leaves the name to wrap around it. `items-start`
            left-aligns it with the rank circle underneath. */}
        <div className="flex flex-col-reverse items-start gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
          <div className="flex items-center gap-3">
            <span
              className={mtc(
                'label-sm flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                accent.circleBg,
                accent.circleText
              )}
            >
              {rank + 1}
            </span>
            <Text
              variant={TypographyStyles.DISPLAY_XXS}
              color={TextColors.HEADER}
            >
              {bucket.label}
            </Text>
          </div>
          {bucket.isPrimary && stakeStyle && (
            <Pill
              variant={stakeStyle.variant}
              size="sm"
              color={stakeStyle.color}
            >
              {bucket.stakeLabel}
            </Pill>
          )}
        </div>
        {bucket.isPrimary && bucket.narrative && (
          <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
            {bucket.narrative}
          </Text>
        )}
      </div>
      {bucket.cards.length > 0 && (
        <div className="flex flex-col gap-3" data-report-print-obligations>
          {bucket.cards.map((card, index) => (
            <ObligationStep
              key={card.templateKey}
              card={card}
              tier={tier}
              highStakes={highStakes}
              area={bucket.key}
              position={index + 1}
            />
          ))}
        </div>
      )}
      {bucket.isPrimary && bucket.benefit && (
        <div className="flex items-center gap-2 border-t border-neutral-100 pt-3">
          <Icon
            icon={faShieldCheckRegular}
            size="sm"
            color="text-neutral-500"
            backgroundClassName="h-auto w-auto shrink-0"
          />
          <Text variant={TypographyStyles.LABEL_SM}>{bucket.benefit}</Text>
        </div>
      )}
    </div>
  )
}

const StatusGroup: React.FC<{
  tier: GroupTier
  buckets: AreaBucket[]
  open: boolean
  onToggle: () => void
}> = ({ tier, buckets, open, onToggle }) => {
  const expandRef = useExpandCollapse(open)
  const meta = GROUP_META[tier]
  if (buckets.length === 0) return null
  const total = buckets.reduce(
    (n, b) => n + b.cards.reduce((m, c) => m + c.instances.length, 0),
    0
  )
  return (
    <section
      id={`group-${tier}`}
      className="flex scroll-mt-6 flex-col gap-3"
      data-report-print-small-group={total === 1 ? true : undefined}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        data-report-print-heading
        data-report-print-static
        className="-m-1 flex w-full items-center justify-between gap-2 rounded-lg p-1 text-left transition hover:bg-neutral-25"
      >
        <span className="flex items-center gap-2">
          <span className="flex items-center gap-0.5">
            <Icon icon={meta.icon} size="1x" color={meta.color} />
            <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
              {meta.title}
            </Text>
          </span>
          {/* Omitted at zero rather than rendering "0 items". */}
          {total > 0 && (
            <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
              {`${total} ${total === 1 ? 'item' : 'items'}`}
            </Text>
          )}
        </span>
        <span data-report-print-affordance>
          <Icon
            icon={faChevronDown}
            size="2xs"
            color="text-neutral-400"
            className={mtc('transition-transform', open && 'rotate-180')}
          />
        </span>
      </button>
      <div ref={expandRef} data-report-print-expand>
        <div className="flex flex-col gap-4">
          {buckets.map((b, i) => (
            <AreaObligationGroup key={b.key} bucket={b} tier={tier} rank={i} />
          ))}
        </div>
      </div>
    </section>
  )
}

/**
 * Ticks live in component state and do not survive a reload: the report is
 * composed just-in-time and never stored, so there is nowhere to hang one.
 */
export const ReportView: React.FC<{ report: ComplianceReport }> = ({
  report,
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  useUnmappedConsequenceReport(report.areas)
  const { slot: endCardSlot } = useReportEndCardSlot()
  const [openGroups, setOpenGroups] = useState<Record<GroupTier, boolean>>({
    attention: GROUP_META.attention.defaultOpen,
    needs_info: GROUP_META.needs_info.defaultOpen,
    covered: GROUP_META.covered.defaultOpen,
    not_relevant: GROUP_META.not_relevant.defaultOpen,
  })
  const { counts } = report
  const licenses = useReselector(selectLicenses)
  const verdict = useComplianceVerdict()
  const licensedStates = licensedStateCodes(
    licenses,
    report.context.jurisdictions
  )
  const {
    trackViewedReportPage,
    trackJumpedToReportStatus,
    trackOpenedReportPrintDialog,
  } = useComplianceAnalytics(ANALYTICS_SURFACES.SignupFunnel)
  const viewedRef = useRef(false)

  useEffect(() => {
    if (viewedRef.current) return
    viewedRef.current = true
    trackViewedReportPage({
      attention_count: counts.attention,
      needs_info_count: counts.needsInfo,
      good_to_know_count: counts.goodToKnow,
      covered_count: counts.covered,
      not_relevant_count: counts.notRelevant,
      licensed_states: licensedStates,
      verdict,
      is_pql: report.isPql,
      narrative_source: report.narrativeSource,
    })
    // The page is reported once, from the state it was first rendered in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const jumpTo = (tier: GroupTier): void => {
    trackJumpedToReportStatus({ status: tier })
    setOpenGroups((prev) => ({ ...prev, [tier]: true }))
    document
      .getElementById(`group-${tier}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  /** Back into the intake, starting from the top. There used to be one of
   *  these per area, each jumping to the section that area's questions came
   *  from, but a row of identical buttons down the page competed with the
   *  report's own content, and the report is read as a whole rather than area
   *  by area. From the first section the user can reach any answer, and the
   *  nav shows where they are. */
  const goToIntake = (): void => {
    dispatch(setSectionIndex(0))
    navigate(COMPLIANCE_PATHS.intake)
  }
  const printReport = (): void => {
    trackOpenedReportPrintDialog()
    window.print()
  }
  return (
    <div className={FUNNEL_PAGE_CLS} data-report-print-root>
      <ReportHero report={report} onJump={jumpTo} />
      <div className={mtc('mt-3', REPORT_GRID_CLS)} data-report-print-grid>
        {/* Stacked below `sm`: side by side, "Your practice areas" wraps to two
            lines to make room for the button, so the heading reads as broken.
            The button takes its own line underneath, left-aligned with it. */}
        <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 lg:col-start-1 lg:row-start-1">
          <Text variant={TypographyStyles.DISPLAY_XS} color={TextColors.HEADER}>
            Your practice areas
          </Text>
          <div className="flex flex-wrap gap-2" data-print-exclude>
            <Button
              variant="secondary"
              size="sm"
              iconLeft={faPen}
              onClick={goToIntake}
            >
              Update Answers
            </Button>
            <Button variant="primary" size="sm" onClick={printReport}>
              Print or Save Report
            </Button>
          </div>
        </div>
        <div className="flex flex-col gap-8 lg:col-start-1 lg:row-start-2">
          {GROUP_TIERS.map((tier) => (
            <StatusGroup
              key={tier}
              tier={tier}
              buckets={bucketAreasByTier(report.areas, tier)}
              open={openGroups[tier]}
              onToggle={() =>
                setOpenGroups((prev) => ({ ...prev, [tier]: !prev[tier] }))
              }
            />
          ))}
        </div>
        <aside
          className="flex flex-col gap-4 lg:col-start-2 lg:row-span-2 lg:row-start-1"
          data-print-exclude
        >
          {/* Whichever card the rail carries, pinned while the obligations
              scroll past so the way to act on the report is always in reach,
              but only where pinning is worth it. Sticky fails silently: an
              element taller than the scrollport never pins, it just scrolls
              away, and nothing about that shows until someone opens the page on
              a short window. So it only applies above a viewport height that
              can seat the card, and the height cap backstops it if the copy
              ever grows.

              `100vh` is exact rather than approximate here: Layout is
              `h-screen` and `main` is `h-full overflow-y-auto` with no header
              above it, so the scrollport is the viewport. The 3rem is the
              `top-6` offset plus the same again as bottom slack. */}
          {endCardSlot === 'end' && (
            <div className={mtc(STICKY_CTA_CLS, 'lg:pl-8')}>
              {/* The card's own `mx-4` and the `Card` atom's `my-4` are both page
                  positioning: the margin pushes it past a 300px rail's edge and
                  drops its top below the heading row it should sit level with.

                  The gap off the obligations column is `lg:pl-8` on this
                  wrapper rather than a margin on the card: the card is
                  `w-full`, so a margin adds to its width and pushes it past the
                  rail's right edge instead of insetting it. `lg` only, since
                  that is where the aside becomes the second column. */}
              <KeepInTouchCard className="m-0" />
            </div>
          )}
          {endCardSlot === 'rail' && (
            // Tagged rather than ref'd: the sticky bar watches this element itself, so the
            // boolean it derives lives with the bar instead of here, where every crossing
            // of this card's edge re-rendered the whole report for it.
            <div
              id={REPORT_END_CARD_ID}
              className={STICKY_CTA_CLS}
              data-print-exclude
            >
              <ReportEndCard
                attentionCount={counts.attention}
                isPql={report.isPql}
              />
            </div>
          )}
        </aside>
      </div>
      {/* A decided rejection is placed here rather than pinned above: pinned, it
          would stay on screen for the whole read, and a rejection the reader
          arrives at after their findings is a different thing from one that
          follows them down the page. */}
      {endCardSlot === 'end' && (
        <div data-print-exclude>
          <ReportEndCard
            attentionCount={counts.attention}
            isPql={report.isPql}
          />
        </div>
      )}
      <ReportDisclaimerFooter generatedAt={report.generatedAt} />
      {/* Only alongside the next-steps card, which is the `rail` slot. `end` is a decided
          rejection and `none` an entitled customer — neither has actions to shortcut to.
          The bar clears itself out of the way of the disclaimer footer by standing down as
          soon as the card it points at is on screen, which happens before the footer is. */}
      {endCardSlot === 'rail' && (
        <div data-print-exclude>
          <ReportStickyCta isPql={report.isPql} />
        </div>
      )}
    </div>
  )
}
